import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Drawer, Form, Localization, Responsive } from 'connex-cds';
import { find } from 'lodash';
import { useTicketContext } from '../../../TicketContext';
import { TicketDetailDesktop } from './TicketDetailDesktop';
import { TicketDetailMobile } from './TicketDetailMobile';
import { activeFormsAndWaiversArray } from '../../../getActiveForms';
import {
  useCompanySetup,
  useFormsAndWaiversSetup,
  useVehicleSetup,
  useVehicleTypeSetup,
} from '../../../MasterDataProvider';
import { useGetDriverAttachment } from '../../../../../../../query-hooks/driver-attachment';
import { masterData, mobileTicket } from '../../../../../../../api';
import { DEFAULT_COC_SETUP_TYPE } from '../../../cocSetupDefaults';

export const TicketDetail = props => {
  const navigate = useNavigate();
  const { entityRef, truckNumber } = useParams();
  const queryClient = useQueryClient();
  const { translateObjects } = Localization.useTranslateObjects();
  const { userLocale } = Localization.useLocalizationContext();
  const { validateForm, resetForm } = Form.useFormContext();
  const { ticket } = useTicketContext();
  const formsAndWaiversSetup = useFormsAndWaiversSetup();
  const companySetup = useCompanySetup();
  const vehicleSetup = useVehicleSetup();
  const vehicleTypeSetup = useVehicleTypeSetup();
  const { data: attachments } = useGetDriverAttachment(ticket?.crn) || [];

  const vehicleTypeData = React.useMemo(() => {
    const targetVehicle = find(vehicleSetup?.data, { id: ticket?.vehicleId });

    return find(vehicleTypeSetup?.data, { crn: targetVehicle?.vehicleType?.vehicleTypeRef });
  }, [vehicleSetup.data, vehicleTypeSetup.data, ticket?.vehicleId]);

  const unloadingMethodEnabled = React.useMemo(() => {
    if (!companySetup?.data?.isUnloadingMethodEnabled) {
      return false;
    } else if (
      vehicleTypeData &&
      companySetup?.data?.isUnloadingMethodEnabled !== false &&
      vehicleTypeData?.isUnloadingMethodEnabled !== true
    ) {
      return false;
    }

    return ticket?.customData?.ticketType !== 'PMP';
  }, [companySetup?.data?.isUnloadingMethodEnabled, ticket?.customData?.ticketType, vehicleTypeData]);

  const toggleEarlyAcceptance = useCallback(() => {
    if (companySetup?.data?.isEarlySignatureEnabled) {
      navigate({
        pathname: '../finalize',
        search: new URLSearchParams({ showEarlyAcceptance: 'true' }).toString(),
      });
    }
    return false;
  }, [companySetup?.data?.isEarlySignatureEnabled]);

  const { isLoading } = props;

  React.useEffect(() => {
    validateForm();
  }, [validateForm]);

  React.useEffect(() => {
    if (ticket === null) {
      resetForm({ reinitialize: true });
    }
  }, [resetForm, ticket]);

  React.useEffect(() => {
    if (!entityRef) {
      return;
    }

    queryClient.prefetchQuery({
      queryKey: ['setup', entityRef, 'templates-mt'],
      queryFn: () => masterData.getMasterData({ entityRef, typeId: 'templates-mt', truckNumber }),
      staleTime: Infinity,
    });
  }, [entityRef, queryClient, truckNumber]);

  React.useEffect(() => {
    const ticketRef = ticket?.crn;
    if (!entityRef || !ticketRef || companySetup?.isLoading) {
      return;
    }
    const cocSetupType = companySetup?.data?.batchMaterialSummaryTable ?? DEFAULT_COC_SETUP_TYPE;

    queryClient.prefetchQuery({
      queryKey: ['setup', entityRef, 'coc-setup', ticketRef, cocSetupType],
      queryFn: () =>
        mobileTicket.getCocSetup({
          entityRef,
          ticketRef,
          queryParams: { coc_setup: cocSetupType },
        }),
      staleTime: Infinity,
    });
  }, [companySetup?.data?.batchMaterialSummaryTable, companySetup?.isLoading, entityRef, queryClient, ticket?.crn]);

  const activeFormsAndWaivers = React.useMemo(
    () =>
      activeFormsAndWaiversArray({
        ticket,
        formTypeCondition: 'DAMAGE',
        formsAndWaiversSetup,
        vehicleSetup,
        vehicleTypeSetup,
        companySetup,
        translateObjects,
        userLocale,
      }),
    [ticket, formsAndWaiversSetup, vehicleSetup, vehicleTypeSetup, companySetup, translateObjects, userLocale]
  );

  return (
    <Drawer.DrawerProvider>
      <Responsive>
        <TicketDetailMobile
          activeFormsAndWaivers={activeFormsAndWaivers}
          isLoading={isLoading}
          attachments={attachments}
          toggleEarlyAcceptance={toggleEarlyAcceptance}
          unloadingMethodEnabled={unloadingMethodEnabled}
        />
        <TicketDetailDesktop
          activeFormsAndWaivers={activeFormsAndWaivers}
          isLoading={isLoading}
          attachments={attachments}
          toggleEarlyAcceptance={toggleEarlyAcceptance}
          unloadingMethodEnabled={unloadingMethodEnabled}
        />
      </Responsive>
    </Drawer.DrawerProvider>
  );
};
